<script lang="ts" setup>
import { onMounted, reactive, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { AccountDetails, AccountFacets, type AccountListFilter, type PaginatedAccounts } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import UserTable from '@/components/dashboard/Admin/AdminUserManagement/UserTable.vue'
import UserEdit from '@/components/dashboard/Admin/AdminUserManagement/UserEdit.vue'
import { useAxios } from '@/composables/useAxios'
import { STATUS_CODES } from '@/constants'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'

const pagination = usePagination({ initialItemsPerPage: 50 })

// Each pill narrows on exactly one of the two query parameters the endpoint
// takes. The mapping is exhaustive by type, so a new filter cannot quietly fall
// off the request.
const FILTER_PARAM: Record<Exclude<AccountListFilter, 'all'>, 'role' | 'status'> = {
  active: 'status',
  inactive: 'status',
  unverified: 'status',
  admin: 'role',
  reseller: 'role',
  customer: 'role'
}

const data = reactive({
  loading: false,
  users: [] as AccountDetails[],
  editUser: null as AccountDetails | null,
  search: '',
  filter: 'all' as AccountListFilter,
  facets: null as AccountFacets | null
})

const buildQuery = (): string => {
  const { limit, offset } = pagination.buildQueryParams()
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })

  const term = data.search.trim()
  if (term) params.set('search', term)
  if (data.filter !== 'all') params.set(FILTER_PARAM[data.filter], data.filter)

  return params.toString()
}

// Responses are not guaranteed to land in the order they were sent, and the
// pills stay clickable while a request is out, so an older reply could overwrite
// a newer one: toggling a filter off and back on could leave the narrowed rows
// on screen with no filter lit. Only the newest request may write.
let latestRequest = 0

const loadUsers = () => {
  const request = ++latestRequest
  data.loading = true

  useAxios<null, PaginatedAccounts>('GET', `/account/all?${buildQuery()}`, true)
    .then((res) => {
      if (!res || request !== latestRequest) return
      if (res.status === STATUS_CODES.OK && res.data) {
        const validate = AccountDetails.array().safeParse(res.data.data)
        if (!validate.success) return
        data.users = validate.data
        const parsedFacets = AccountFacets.safeParse(res.data.stats)
        data.facets = parsedFacets.success ? parsedFacets.data : null
        pagination.setTotal(res.data.total)
      }
    })
    .catch((error) => {
      // A superseded request failing is not news, and reporting it would file
      // the noise of every discarded request.
      if (request === latestRequest) {
        handleAxiosError(error, { logPrefix: 'UserManagement:loadUsers' })
      }
    })
    .finally(() => {
      // Otherwise a superseded response clears the spinner while the current
      // request is still out.
      if (request === latestRequest) data.loading = false
    })
}

watch(() => pagination.page.value, () => {
  loadUsers()
})

// A narrowed result rarely has the page the operator was on, so go back to the
// first. goToPage only fires the page watcher when the page actually changes,
// hence loading here when it does not: otherwise the request is either lost or
// sent twice.
const reloadFromFirstPage = () => {
  if (pagination.page.value === 1) {
    loadUsers()
  } else {
    pagination.goToPage(1)
  }
}

const searchUsers = useDebounceFn(reloadFromFirstPage, 300)

watch(() => data.search, () => {
  searchUsers()
})

const handleFilter = (filter: AccountListFilter) => {
  data.filter = filter
  reloadFromFirstPage()
}

const handleCreate = () => {
  data.editUser = {} as AccountDetails
}

const handleCloseEdit = () => {
  data.editUser = null
}

const handleSaved = () => {
  loadUsers()
}

onMounted(() => {
  loadUsers()
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.users.title')"
    :subtitle="$t('dashboard.admin.users.subtitle')"
    icon="mdi-account-supervisor"
    icon-color="cyan"
  >
    <template #actions>
      <div class="header-actions">
        <div class="search-box">
          <v-icon icon="mdi-magnify" size="18" />
          <input
            v-model="data.search"
            type="text"
            :placeholder="$t('dashboard.admin.users.table.search')"
            class="search-input"
          />
          <button
            v-if="data.search"
            class="clear-btn"
            @click="data.search = ''"
          >
            <v-icon icon="mdi-close" size="16" />
          </button>
        </div>

        <button class="create-btn" @click="handleCreate">
          <v-icon icon="mdi-account-plus" size="18" />
          <span>{{ $t('dashboard.admin.users.newUser') }}</span>
        </button>
      </div>
    </template>

    <!-- User Table -->
    <UserTable
      :users="data.users"
      :loading="data.loading"
      :total="pagination.total.value"
      :page="pagination.page.value"
      :items-per-page="pagination.itemsPerPage.value"
      :total-pages="pagination.totalPages.value"
      :search="data.search"
      :facets="data.facets"
      @refresh="loadUsers"
      @update:page="pagination.goToPage"
      @update:items-per-page="pagination.setItemsPerPage"
      @update:filter="handleFilter"
    />

    <!-- Edit Panel -->
    <UserEdit
      :user="data.editUser"
      mode="create"
      @close="handleCloseEdit"
      @saved="handleSaved"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Header Actions */
.header-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--dashboard-gap-md, 10px);
}

/* Search Box */
.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: var(--dashboard-input-radius, 8px);
  width: 200px;
  transition: all 0.2s ease;

  .v-icon {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus-within {
    border-color: rgba(0, 176, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  cursor: pointer;

  .v-icon {
    color: rgba(200, 218, 255, 0.6);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

/* Create Button */
.create-btn {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm, 8px);
  padding: var(--dashboard-btn-padding, 8px 12px);
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.25);
  border-radius: var(--dashboard-btn-radius, 8px);
  color: #4CAF50;
  font-size: var(--dashboard-subtitle-size, 0.8rem);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.15);
    border-color: rgba(76, 175, 80, 0.35);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    width: 100%;
  }

  .search-box {
    width: 100%;
  }

  .create-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .create-btn,
  .search-box {
    transition: none;

    &:hover {
      transform: none;
    }
  }
}
</style>
